import '../css/app.css';
import '../css/media.css';

import {createApp, h} from 'vue';
import {createInertiaApp} from '@inertiajs/inertia-vue3';
import {InertiaProgress} from '@inertiajs/progress';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {ZiggyVue} from '../../vendor/tightenco/ziggy/dist/index.esm.js';
import axios from 'axios';

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

import * as Sentry from "@sentry/vue";

// Configure axios with Inertia's shared data approach for CSRF
axios.defaults.withCredentials = true;

// Apply brand theme CSS variables from page props
function applyBrandTheme(props) {
  const theme = props?.brandInfo?.theme;
  if (!theme?.accent) return;

  const root = document.documentElement;
  Object.entries(theme.accent).forEach(([shade, rgb]) => {
    root.style.setProperty(`--brand-accent-${shade}`, rgb);
  });
}

// GA4 single-page-app pageview tracking.
// The base gtag config in app.blade.php sends the initial landing page_view
// automatically. Inertia then swaps pages client-side with no browser reload,
// so GA4 would otherwise never see those internal navigations. We send a
// page_view on each navigation whose path differs from the last one counted —
// this captures internal navigation without double-counting the landing page
// (Inertia also fires `navigate` for the initial visit, which we record in
// `inertia:init` so it is ignored here).
let lastTrackedGaPath = null;

function currentPath(event) {
  return event?.detail?.page?.url || (window.location.pathname + window.location.search);
}

function trackGaPageView(url) {
  if (typeof window.gtag !== 'function') return;
  if (!url || url === lastTrackedGaPath) return;
  lastTrackedGaPath = url;
  window.gtag('event', 'page_view', {
    page_path: url,
    page_location: window.location.origin + url,
    page_title: document.title,
  });
}

// Use Inertia's shared page props for CSRF token and brand theme
document.addEventListener('inertia:init', (event) => {
  // Access the csrf_token from the shared page props
  const csrfToken = event.detail.page.props.csrf_token || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
  }

  // Apply brand theme on initial load
  applyBrandTheme(event.detail.page.props);

  // Record the landing path: the base tag already counted it as a page_view,
  // so the first `inertia:navigate` (which fires for the initial visit) is a
  // no-op rather than a duplicate.
  lastTrackedGaPath = currentPath(event);
});

// Re-apply brand theme and report the GA4 pageview on each SPA navigation.
document.addEventListener('inertia:navigate', (event) => {
  applyBrandTheme(event.detail.page.props);
  trackGaPageView(currentPath(event));
});

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'BusinessCertificateServices.com';

// Script origins whose errors we can never act on: browser extensions and
// third-party tags we don't ship. Used both as Sentry's `denyUrls` (which
// matches the bottom stack frame) and by hasNoActionableFrame() below.
const THIRD_PARTY_ERROR_SOURCES = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^chrome:\/\//i,
  /^webkit-masked-url:/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /connect\.facebook\.net/i,
  /unpkg\.com/i,
];

/**
 * True when every frame in the stack is unfixable — either "<anonymous>"
 * (code eval'd or injected by an extension, which has no source file) or a
 * third-party origin.
 *
 * This is what catches errors like the "Right-hand side of 'instanceof' is
 * not an object" TypeError: its stack is `<anonymous>` / `Array.some` /
 * `<anonymous>` with zero frames pointing at our bundle, so there is nothing
 * in our code to fix. denyUrls alone misses it because injected scripts have
 * no URL to match against.
 *
 * Events carrying no stack at all are kept — those can still be real.
 *
 * @param {Object} event Sentry event
 * @returns {boolean}
 */
function hasNoActionableFrame(event) {
  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? []
  );

  if (frames.length === 0) {
    return false;
  }

  return !frames.some((frame) => {
    const path = frame.abs_path || frame.filename || '';

    // Native builtins (Array.some) and eval'd/injected code report no usable
    // path, so they can never be the frame that vindicates an event.
    if (!path || path === '<anonymous>' || path === '[native code]') {
      return false;
    }

    return !THIRD_PARTY_ERROR_SOURCES.some((pattern) => pattern.test(path));
  });
}

createInertiaApp({
  title: (title) => `${title}`,
  resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
  setup({el, app, props, plugin}) {
    const vueApp = createApp({render: () => h(app, props)});

    // Determine if we're in dev environment
    const isDev =
      window.location.hostname.includes('.test');
    const isAdmin = window.location.href.includes('admin');
    // Create integrations array
    const integrations = [];

    // Sentry Session Replay — production only, skip admin pages.
    if (!isDev && !isAdmin) {
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          block: [
            'input[type="password"]',
            '[name*="ein"]',
            '[name*="ssn"]',
            '[name*="social"]',
            '[name*="tax_id"]',
            '#ein',
            '#ssn',
            '#tax-id',
          ],
        })
      );
    }

    Sentry.init({
      app: vueApp,
      dsn: "https://93a1c0fb92da2ec26ee6180f4807ccd6@o4507189048901632.ingest.us.sentry.io/4508044116361216",
      integrations: integrations,
      // Session Replay settings
      replaysSessionSampleRate: 1.0, // Record 100% of sessions
      replaysOnErrorSampleRate: 1.0, // Always record sessions with errors
      // Filter out browser extension errors (LastPass, etc.)
      ignoreErrors: [
        'Object Not Found Matching Id',
        /MethodName:update/,
      ],
      // Drops events whose bottom frame belongs to an extension or a
      // third-party tag. Injected scripts with no URL slip past this and are
      // caught by hasNoActionableFrame() in beforeSend instead.
      denyUrls: THIRD_PARTY_ERROR_SOURCES,
      beforeSend(event) {
        // An exception event carries its text at exception.values[].value —
        // event.message is undefined for those, so reading only `message`
        // (as this filter previously did) never matched a thrown error.
        const description = event.exception?.values?.[0]?.value ?? event.message ?? '';

        if (description.includes('Object Not Found Matching Id')) {
          return null;
        }

        if (hasNoActionableFrame(event)) {
          return null;
        }

        return event;
      },
    });

    vueApp
      .use(plugin)
      .use(ZiggyVue, Ziggy)
      .use(Toast, {
        transition: "Vue-Toastification__bounce",
        maxToasts: 3,
        newestOnTop: true
      })
      .mixin({methods: {route}});

    return vueApp.mount(el);
  },
});

InertiaProgress.init({color: '#4B5563'});